/**
 * @author: chenlv
 * @date: 2025/01/27
 * @desc: SSE (Server-Sent Events) 下载工具函数
 */

import { merge } from 'lodash-es';
import { getAuthorization } from '@/utils/utils';

/**
 * SSE 事件类型
 */
export enum SSEEventType {
  DATA = 'data',
  ERROR = 'error',
  COMPLETE = 'complete',
  PROGRESS = 'progress',
}

/**
 * SSE 响应数据接口
 */
export interface SSEResponse {
  list: any[];
  total: number;
  has_more: boolean;
  ext: {
    market_info: IRecord<IRecord<string>>;
  };
  [key: string]: any;
}

/**
 * SSE 事件消息接口
 */
export interface SSEMessage {
  event?: SSEEventType;
  data: SSEResponse | string;
  progress?: number;
}

/**
 * SSE 下载配置接口
 */
export interface SSEDownloadConfig {
  /** 请求 URL */
  url: string;
  /** 请求参数 */
  params: IRecord;
  /** 数据接收回调 */
  onData: (data: SSEResponse) => void;
  /** 进度回调 */
  onProgress?: (progress: number, current: number, total: number) => void;
  /** 完成回调 */
  onComplete?: () => void;
  /** 错误回调 */
  onError?: (error: Error) => void;
  /** 取消信号 */
  signal?: AbortSignal;
}

/**
 * 使用 Fetch API 进行 SSE 流式下载
 *
 * @param config - SSE 下载配置
 * @returns Promise<void>
 *
 * @example
 * ```typescript
 * await fetchSSEDownload({
 *   url: '/api/v1/intelligence_pc/game/genre_profile/metrics',
 *   params: { ... },
 *   onData: (data) => console.log('Received data:', data),
 *   onProgress: (progress) => console.log('Progress:', progress),
 *   onComplete: () => console.log('Download complete'),
 *   onError: (error) => console.error('Download error:', error),
 * });
 * ```
 */
export const fetchSSEDownload = async (config: SSEDownloadConfig): Promise<SSEResponse> => {
  const { url, params, signal, onError } = config;

  try {
    const Authorization = getAuthorization();
    const headers: IRecord<string> = {
      'Content-Type': 'application/json',
    };
    if (Authorization) {
      headers.Authorization = Authorization;
    }

    const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}is_download=true`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    const accumulatedData: SSEResponse = { list: [], total: 0, has_more: false, ext: { market_info: {} } };
    let bufferRemainder = '';
    let partialData = '';
    let fullBody = '';

    loop: while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // 解码并追加到缓冲区
      const buffer = decoder.decode(value, { stream: true });
      fullBody += buffer;
      const combined = bufferRemainder + buffer;
      const lines = combined.split('\n');
      bufferRemainder = lines.pop() || '';

      // 如果是 SSE 格式，可能需要解析 data: 行
      for (const chunk of lines) {
        if (chunk.startsWith('data:')) {
          const dataPart = chunk.substring(5).trim();
          if (!dataPart) continue;
          partialData += dataPart;
        }

        // 空行表示一次 SSE 消息结束，尝试解析
        if (chunk.trim() === '' && partialData) {
          try {
            const obj = JSON.parse(partialData);
            partialData = '';
            if (obj.done) {
              break loop;
            }

            const normalizedChunk = normalizeDownloadResponse(obj);
            if (normalizedChunk) {
              accumulatedData.list = [
                ...accumulatedData.list,
                ...normalizedChunk.list,
              ];
              accumulatedData.total = normalizedChunk.total;
              accumulatedData.ext = merge(accumulatedData.ext, normalizedChunk.ext);
              accumulatedData.$ext = merge(
                accumulatedData.$ext || {},
                normalizedChunk.$ext || normalizedChunk.ext || {},
              );
              continue;
            }

            const chunkList = obj?.list || [];
            const ext = obj?.ext || {};
            accumulatedData.list = [...accumulatedData.list, ...chunkList];
            accumulatedData.total = accumulatedData.total + chunkList.length;
            accumulatedData.ext = merge(accumulatedData.ext, ext);
            accumulatedData.$ext = merge(accumulatedData.$ext || {}, ext);
            // console.debug('SSE数据:', obj);
          } catch (parseErr) {
            console.warn('SSE JSON parse failed, waiting for more data', parseErr);
          }
        }
      }
    }

    // 处理循环退出后仍有未解析的分片
    if (partialData) {
      try {
        const obj = JSON.parse(partialData);
        const normalizedChunk = normalizeDownloadResponse(obj);
        if (normalizedChunk) {
          accumulatedData.list = [
            ...accumulatedData.list,
            ...normalizedChunk.list,
          ];
          accumulatedData.total = normalizedChunk.total;
          accumulatedData.ext = merge(accumulatedData.ext, normalizedChunk.ext);
          accumulatedData.$ext = merge(
            accumulatedData.$ext || {},
            normalizedChunk.$ext || normalizedChunk.ext || {},
          );
        } else {
          const chunkList = obj?.list || [];
          const ext = obj?.ext || {};
          accumulatedData.list = [...accumulatedData.list, ...chunkList];
          accumulatedData.total = accumulatedData.total + chunkList.length;
          accumulatedData.ext = merge(accumulatedData.ext, ext);
          accumulatedData.$ext = merge(accumulatedData.$ext || {}, ext);
        }
      } catch (parseErr) {
        console.warn('SSE JSON parse failed on remaining data', parseErr);
      }
    }

    if (!accumulatedData.list.length) {
      const fallbackResponse = parseDownloadResponseBody(fullBody);
      if (fallbackResponse) {
        return fallbackResponse;
      }
    }

    return accumulatedData;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('SSE download was aborted');
      throw error;
    }
    onError?.(error as Error);
    throw error;
  }
};

/**
 * 使用 EventSource 进行 SSE 下载（备用方案）
 * 注意：EventSource 只支持 GET 请求，且不支持自定义请求头
 *
 * @param url - 请求 URL（需包含所有参数）
 * @param callbacks - 回调函数集合
 * @returns EventSource 实例
 */
export const createEventSourceDownload = (
  url: string,
  callbacks: {
    onData: (data: SSEResponse) => void;
    onProgress?: (progress: number, current: number, total: number) => void;
    onComplete?: () => void;
    onError?: (error: Error) => void;
  },
): EventSource => {
  const eventSource = new EventSource(url);
  let totalRecords = 0;
  let totalExpected = 0;

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as SSEResponse;

      if (Array.isArray(data.list)) {
        totalRecords += data.list.length;
        callbacks.onData(data);
      }

      totalExpected = data.total || totalExpected;

      if (callbacks.onProgress && totalExpected > 0) {
        const progress = Math.min((totalRecords / totalExpected) * 100, 100);
        callbacks.onProgress(progress, totalRecords, totalExpected);
      }

      if (!data.has_more) {
        callbacks.onComplete?.();
        eventSource.close();
      }
    } catch (error) {
      callbacks.onError?.(error as Error);
      eventSource.close();
    }
  };

  eventSource.onerror = (error) => {
    callbacks.onError?.(new Error(`EventSource error: ${error}`));
    eventSource.close();
  };

  return eventSource;
};

/**
 * 检查下载配置是否应该使用 SSE 模式
 * @param downloadApiParams - 下载 API 参数
 * @returns 是否使用 SSE 模式
 */
export const shouldUseSSE = (downloadApiParams: IRecord): boolean => {
  return !!downloadApiParams?.use_sse_page;
};

/**
 * 获取 SSE 下载配置
 * @param downloadApiParams - 下载 API 参数
 * @returns SSE 配置对象
 */
export const getSSEConfig = (downloadApiParams: IRecord): { max_size: number } | undefined => {
  return downloadApiParams?.use_sse_page;
};

const normalizeDownloadResponse = (root: IRecord): SSEResponse | null => {
  const dataNode = root?.data ?? root;
  const list = dataNode?.list;

  if (!Array.isArray(list)) {
    return null;
  }

  const ext = root?.ext ?? dataNode?.ext ?? {};

  return {
    list,
    total: Number(dataNode?.total) || list.length,
    has_more: false,
    ext,
    $ext: ext,
  };
};

const parseDownloadResponseBody = (body: string): SSEResponse | null => {
  const trimmedBody = body.trim();
  if (!trimmedBody) {
    return null;
  }

  try {
    return normalizeDownloadResponse(JSON.parse(trimmedBody));
  } catch (error) {
    return null;
  }
};
